import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import useGameStore from '@store/useGameStore';
import useSelectionStore from '@store/useSelectionStore';
import { ClickContext } from '@utils/enums';
import feedback from '@components/ui/general/feedback/feedback';
import { CONFIG } from '@utils/constants';
import { resetToDeployApi, randomizeEnemyPositionsApi } from '@api/debugApi';
import { Bug, RotateCcw, Shuffle, Skull, MessageSquareWarning } from 'lucide-react';
import BugReportModal from '@components/developer/BugReportModal';
import './DebugPanel.css';

/**
 * Debug Panel Component
 * Shows debug controls when in debug mode and in a game
 */
export default function DebugPanel() {
  const location = useLocation();
  const { gameId } = useParams();
  const game = useGameStore((state) => state.game);
  const isDeployPhase = useGameStore((state) => state.isDeployPhase);
  const { clickContext, setClickContext } = useSelectionStore();
  const [isKillMode, setIsKillMode] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);

  // Handle kill mode toggle
  const handleToggleKillMode = () => {
    if (isKillMode) {
      // Exit kill mode
      setClickContext(ClickContext.SELECT);
      setIsKillMode(false);
      feedback.info('Kill Mode deactivated');
    } else {
      // Enter kill mode
      setClickContext(ClickContext.DEBUG_KILL);
      setIsKillMode(true);
      feedback.warning('🔪 Kill Mode ACTIVATED - Click any unit to kill it');
    }
  };

  // Sync kill mode state with context
  useEffect(() => {
    if (clickContext !== ClickContext.DEBUG_KILL && isKillMode) {
      setIsKillMode(false);
    }
  }, [clickContext, isKillMode]);

  // Only show in debug mode and when in a game
  const isInGame = location.pathname.includes('/games/') && gameId;

  if (!CONFIG.DEBUG_MODE) {
    console.warn('[DebugPanel] Not showing: DEBUG_MODE is false');
    return null;
  }
  if (!isInGame) {
    return null;
  }
  if (!game) {
    console.warn('[DebugPanel] Not showing: Game not loaded');
    return null;
  }

  const handleResetToDeploy = async () => {
    try {
      feedback.info('Resetting game to deploy phase...');
      const response = await resetToDeployApi(gameId);
      if (response?.success) {
        feedback.success('Game reset to deploy phase!');
      }
    } catch (error) {
      console.error('[DebugPanel] Reset to deploy failed:', error);
      feedback.error('Failed to reset game: ' + (error.message || 'Unknown error'));
    }
  };

  const handleRandomizeEnemies = async () => {
    try {
      feedback.info('Randomizing enemy positions...');
      const response = await randomizeEnemyPositionsApi(gameId);
      if (response?.success) {
        feedback.success('Enemy positions randomized!');
      }
    } catch (error) {
      console.error('[DebugPanel] Randomize enemies failed:', error);
      feedback.error('Failed to randomize enemies: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div className="debug-panel">
      <div className="debug-indicator">
        <Bug size={16} />
        <span>DEBUG</span>
      </div>

      <button
        className="button debug-button reset-button"
        onClick={handleResetToDeploy}
        title="Reset game to deployment phase"
      >
        <RotateCcw size={16} />
        <span>Reset Deploy</span>
      </button>

      {isDeployPhase && (
        <button
          className="button debug-button randomize-button"
          onClick={handleRandomizeEnemies}
          title="Redeploy AI unit positions"
        >
          <Shuffle size={16} />
          <span>Redeploy AI</span>
        </button>
      )}

      <button
        className={`button debug-button kill-button ${isKillMode ? 'active' : ''}`}
        onClick={handleToggleKillMode}
        title={isKillMode ? "Click to exit kill mode" : "Activate kill mode - click units to kill them"}
      >
        <Skull size={16} />
        <span>{isKillMode ? 'Kill Mode ON' : 'Kill Mode'}</span>
      </button>

      <button
        className="button debug-button report-button"
        onClick={() => setIsBugReportOpen(true)}
        title="Report a bug or suggestion"
      >
        <MessageSquareWarning size={16} />
        <span>Report</span>
      </button>

      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={() => setIsBugReportOpen(false)}
      />
    </div>
  );
}